/* @flow */
/**
 * Provides utilities for constructing line summary report request metadata.
 * Maps UI-selected report fields and filters to the API field shapes expected in responses.
 */
import lineSummaryReportsHeader from '../../constants/lineSummaryReportsHeader';
import lineSummaryReportFieldProperties from '../../constants/fieldProperties/lineSummaryReportFieldProperties';

/**
 * Appends default and required line summary fields to the supplied request field list.
 */
export function getFieldsList(lineItemSecondaryFields: Object) {
  // Start with the default report header fields displayed by the UI.
  lineSummaryReportsHeader
    .filter((header) => header.default)
    .forEach((header) => {
      lineItemSecondaryFields.push(header.primary);
      if (header.secondary) {
        lineItemSecondaryFields.push(header.secondary);
      }
      if (header.additionalFields) {
        lineItemSecondaryFields.push(...header.additionalFields);
      }
    });
  // Include additional header fields that the API requires even when not shown by default.
  lineSummaryReportsHeader.forEach((headRow: Object) => {
    if (headRow.includeInRequest) {
      lineItemSecondaryFields.push(headRow.primary);
      if (headRow.secondary && !lineItemSecondaryFields.includes(headRow.secondary)) {
        lineItemSecondaryFields.push(headRow.secondary);
      }
    }
  });
}

/**
 * Adds filtersInResponse to a line-item search request from selected line summary filters.
 */
export function getRequestedFilterValuesInLineItemResults(requestBody: Object, state: Object) {
  const requestedBody = requestBody;
  // Request filter dropdown values only for filters currently enabled in line summary state.
  requestedBody.filtersInResponse = Object.keys(state.lineSummaryReportFilters.selectedFilters)
    .filter((filter) => state.lineSummaryReportFilters.selectedFilters[filter])
    .map((filter) => {
      const lineItemFilterInResponse = {};
      const lineItemFieldProperties = lineSummaryReportFieldProperties[filter] || {};
      if (!lineItemFieldProperties) {
        return null;
      }
      lineItemFilterInResponse.primary = lineItemFieldProperties.primary;
      if (lineItemFieldProperties.secondary) {
        lineItemFilterInResponse.secondary = lineItemFieldProperties.secondary;
      }
      return lineItemFilterInResponse;
    })
    // Drop filters that do not resolve to field-property metadata.
    .filter(Boolean);
}
/**
 * Adds filtersInResponse to a request from an explicit list of selected filter keys.
 */
export function getRequestedFilterValues(requestBody: Object, selectedFilterKeys: Object) {
  const requestedBody = requestBody;
  // Translate selected filter keys into API response-field descriptors.
  requestedBody.filtersInResponse = selectedFilterKeys.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = lineSummaryReportFieldProperties[filter] || {};
    if (!fieldProperties) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  })
    .filter(Boolean);
}
/**
 * Adds filtersInResponse to a request using the search panel default filter selections.
 */
export function getSelectedFilterValues(requestBody: Object, state: Object) {
  const requestedBody = requestBody;
  // Use persisted search panel filter preferences when building response filters.
  requestedBody.filtersInResponse = state.searchpaneldata?.selectedFilterOptions
    .map((filter) => {
      const filterInResponse = {};
      const fieldProperties = lineSummaryReportFieldProperties[filter] || {};
      if (!fieldProperties) {
        return null;
      }
      filterInResponse.primary = fieldProperties.primary;
      if (fieldProperties.secondary) {
        filterInResponse.secondary = fieldProperties.secondary;
      }
      return filterInResponse;
    });
}

/**
 * Converts saved filter criteria into filtersInResponse entries for the line summary API.
 */
export function getFiltersInResponseRequestBody(filterData: Object) {
  // Saved filters store fieldName values, so convert each one back to response metadata.
  const filtersInRequestBody = filterData?.map((filter) => {
    const lineItemFilterInResponse = {};
    const lineItemFieldProperties = lineSummaryReportFieldProperties[filter.fieldName] || {};
    if (!lineItemFieldProperties) {
      return null;
    }
    lineItemFilterInResponse.primary = lineItemFieldProperties.primary;
    if (lineItemFieldProperties.secondary) {
      lineItemFilterInResponse.secondary = lineItemFieldProperties.secondary;
    }
    return lineItemFilterInResponse;
  });
  return filtersInRequestBody?.filter((element) => element);
}

/**
 * Converts operational metrics filter keys into line summary response-filter metadata.
 */
export function getSelectedFilterValuesFromOpMetrics(filterValues: Object) {
  // Operational metrics pass only filter keys; the API still needs primary/secondary fields.
  const filtersFromOperationalMetrics = filterValues?.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = lineSummaryReportFieldProperties[filter] || {};
    if (!fieldProperties.primary) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  });
  return filtersFromOperationalMetrics?.filter((element) => element);
}
/**
 * Adds always-requested report fields for operational metrics calls and returns the field list.
 */
export function setFieldsData(isOperationalCall: Boolean, fieldsData: Object) {
  // Operational calls need all fields marked as includeInRequest.
  if (isOperationalCall) {
    lineSummaryReportsHeader.forEach((headRow: Object) => {
      if (headRow.includeInRequest) {
        fieldsData.push(headRow.primary);
        if (headRow.secondary) {
          fieldsData.push(headRow.secondary);
        }
      }
    });
  }
  return fieldsData;
}
/**
 * Builds selected filter metadata and identifies which filter dropdown values must be fetched.
 */
export function getLineItemSelectedFilterKeys(
  selectedFilterKeys: Object,
  modifiedSelectedFilters: Object,
  filterDropdownValues: Object,
  lineItemFiltersToBeRequested: Object,
  filtersInResponse: Object
) {
  selectedFilterKeys.forEach((filter) => {
    const filterRequest: { primary: string; secondary?: string; } = { primary: filter };
    if (lineSummaryReportFieldProperties[filter]?.secondary) {
      filterRequest.secondary = lineSummaryReportFieldProperties[filter].secondary;
    }
    if (modifiedSelectedFilters[filter]) {
      // Fetch dropdown values only when this selected filter has no cached options yet.
      if (!filterDropdownValues[filter]) {
        lineItemFiltersToBeRequested.push(filterRequest);
      }
      filtersInResponse.push(filterRequest);
    }
  });
}
/**
 * Synchronizes selected filter state with the active filter list for line item reports.
 */
export function getLineItemModifiedSelectedFilters(
  modifiedSelectedFilters: Object,
  filtersSelected: Object
) {
  Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
    const modifiedSelectedFiltersNew = modifiedSelectedFilters;
    // Active filters use empty arrays until the user chooses values; inactive filters are nulled.
    if (filtersSelected.includes(filter)) {
      if (!modifiedSelectedFilters[filter]) modifiedSelectedFiltersNew[filter] = [];
    } else {
      modifiedSelectedFiltersNew[filter] = null;
    }
  });
}
